import { getOr, isNull, isUndefined, noop, uniq } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Redirect, RouteComponentProps } from 'react-router';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { ANALYSIS_MARGIN, NAV_BAR, PAGE_MARGIN_PADDING_BORDER, PAGE_VERTICAL_MARGIN_PADDING_BORDER, useSplitView } from '../../../hooks/useSplitView';
import { useWindowResize } from '../../../hooks/useWindowResize';
import { OpinionCommissionedBy } from '../../admin/opinions/store';
import { ClientFeaturePermission } from '../../admin/users/store';
import { getClientHasFeaturePermission } from '../../auth/login/store';
import { AnalysisView, SelectedDocument } from '../../documents/store/types';
import { Analysis, Tabs } from '../../shared/analysis/Analysis';
import { AnalysisSideMenu } from '../../shared/analysis/AnalysisSideMenu';
import { OpinionInstancesWrapper } from '../instances/OpinionInstancesWrapper';
import { getCurrentInstance, getOpinionInstanceTimeline, resetOpinionInstance } from '../instances/store';
import { getAdditionalOpinions, getIsOpening, getLinkedOpinions, getOpinion, getOpinionAnalysisView, getSelectedAdditionalOpinionId, getSelectedOpinionUrl, getSelectedPageNumber, getSourceOpinion, openOpinionAndInstance, resetAllOpinions, setAdditionalOpinionSelected, setSelectedOpinionPage, toggleOpinionAnalysisView } from '../my-opinions/store';
import styles from './OpinionAnalysis.module.scss';
import { DocumentViewerType, resetPdfText } from '../../documents/document-viewer/store';

interface OpinionAnalysisRouteParams {
    opinionId: string | undefined;
}

export const OpinionAnalysis: React.FC<RouteComponentProps<OpinionAnalysisRouteParams>> = ({ match }) => {
    const dispatch = useAppDispatch();
    const [screenWidth, screenHeight] = useWindowResize();
    const opinionInstance = useAppSelector(getCurrentInstance);
    const opinion = useAppSelector(getOpinion);
    const url = useAppSelector(getSelectedOpinionUrl);
    const additionalOpinions = useAppSelector(getAdditionalOpinions);
    const sourceOpinion = useAppSelector(getSourceOpinion);
    const selectedAdditionalOpinionId = useAppSelector(getSelectedAdditionalOpinionId);
    const isOpening = useAppSelector(getIsOpening);
    const opinionAnalysisView = useAppSelector(getOpinionAnalysisView);
    const selectedPage = useAppSelector(getSelectedPageNumber);
    const timeline = useAppSelector(getOpinionInstanceTimeline);
    const linkedOpinions = useAppSelector(getLinkedOpinions);

    const hasViewISDAOpinionPermission = useAppSelector(getClientHasFeaturePermission([ClientFeaturePermission.MEMBER_OF_ISDA]));
    const hasViewICMAOpinionPermission = useAppSelector(getClientHasFeaturePermission([ClientFeaturePermission.MEMBER_OF_ICMA]));
    const hasViewISLAOpinionPermission = useAppSelector(getClientHasFeaturePermission([ClientFeaturePermission.MEMBER_OF_ISLA]));

    const [pageWidth, pageHeight] = useSplitView(opinionAnalysisView, PAGE_MARGIN_PADDING_BORDER, PAGE_VERTICAL_MARGIN_PADDING_BORDER);

    const toggleAnalysisView = useCallback((view: AnalysisView) => view !== opinionAnalysisView && dispatch(toggleOpinionAnalysisView(view)), [dispatch, opinionAnalysisView]);
    const selectAdditionalOpinion = useCallback((opinionId: number | null) => opinionId !== selectedAdditionalOpinionId && dispatch(setAdditionalOpinionSelected(opinionId)), [dispatch, selectedAdditionalOpinionId]);

    const setSelectedPage = useCallback((page: number | null) => dispatch(setSelectedOpinionPage(page)), [dispatch]);

    const allRelevantOpinionIds = useMemo(() => {
        let relevantOpinionIds = [...timeline.map(({ opinionId }) => opinionId), ...linkedOpinions.map(({ opinionId }) => opinionId)];
        if (!isNull(opinion)) {
            relevantOpinionIds.push(opinion.opinionId);
        }
        return uniq(relevantOpinionIds);
    }, [timeline, linkedOpinions, opinion]);

    const additionalTabOptions = useMemo(() => {
        let tabs: Tabs[] = [];
        if (additionalOpinions.length === 0 && isNull(sourceOpinion)) {
            return [{ title: '', onClick: noop, selected: false }];
        }
        if (!isNull(sourceOpinion)) {
            const sourceOpinionTab = {
                title: sourceOpinion.description,
                onClick: () => selectAdditionalOpinion(sourceOpinion.opinionId),
                selected: selectedAdditionalOpinionId === sourceOpinion.opinionId,
                type: 'Source'
            };
            tabs = [sourceOpinionTab];
        }
        if (additionalOpinions.length) {
            const additionalTabs = additionalOpinions.map(({ type, opinionId, description }) => ({
                title: description,
                onClick: () => selectAdditionalOpinion(opinionId),
                selected: selectedAdditionalOpinionId === opinionId,
                type: type || undefined
            }));
            tabs = [...tabs, ...additionalTabs];
        }
        return tabs;
    }, [additionalOpinions, selectedAdditionalOpinionId, selectAdditionalOpinion, sourceOpinion]);

    const tabOptions = useMemo<Tabs[]>(() => [
        {
            title: getOr('', 'description', opinion),
            onClick: () => selectAdditionalOpinion(null),
            selected: isNull(selectedAdditionalOpinionId),
            type: opinion?.type || undefined
        },
        ...additionalTabOptions
    ], [opinion, additionalTabOptions, selectAdditionalOpinion, selectedAdditionalOpinionId]);

    const selectedOpinion = useMemo(() => {
        if (isNull(selectedAdditionalOpinionId)) {
            return opinion;
        }
        if (!isNull(sourceOpinion) && sourceOpinion.opinionId === selectedAdditionalOpinionId) {
            return sourceOpinion;
        }
        return additionalOpinions.find(({ opinionId }) => opinionId === selectedAdditionalOpinionId) || null;
    }, [selectedAdditionalOpinionId, additionalOpinions, opinion, sourceOpinion]);

    const isPermittedToView = useCallback((commissionedBy: OpinionCommissionedBy | string | null) => {
        switch (commissionedBy) {
            case OpinionCommissionedBy.ISDA:
                return hasViewISDAOpinionPermission;
            case OpinionCommissionedBy.ISLA:
                return hasViewISLAOpinionPermission;
            case OpinionCommissionedBy.ICMA:
                return hasViewICMAOpinionPermission;
            default:
                return false;
        }
    }, [hasViewISDAOpinionPermission, hasViewISLAOpinionPermission, hasViewICMAOpinionPermission]);

    const isPermittedToChangeView = useMemo(() => !!opinion && (!!opinion.bespoke || isPermittedToView(opinion.commissionedBy)), [isPermittedToView, opinion]);

    const { params } = match;

    useEffect(() => {
        if ((!opinionInstance && !opinion && params.opinionId) || (!isNull(opinion) && !isNull(opinionInstance) && !isUndefined(params.opinionId) && !allRelevantOpinionIds.includes(parseInt(params.opinionId)))) {
            dispatch(openOpinionAndInstance(params.opinionId));
        }
    }, [params, opinionInstance, dispatch, opinion, allRelevantOpinionIds]);

    useEffect(() => () => {
        dispatch(resetPdfText());
        dispatch(resetAllOpinions());
        dispatch(resetOpinionInstance());
    }, [dispatch]);

    const [width] = useSplitView(opinionAnalysisView, PAGE_MARGIN_PADDING_BORDER + 2, PAGE_VERTICAL_MARGIN_PADDING_BORDER);

    if (!params.opinionId) {
        return (
            <Redirect to='/home' />
        );
    }

    const analysisWrapperDimensions = { height: `${screenHeight - NAV_BAR - ANALYSIS_MARGIN}px`, width: `${screenWidth}px` };
    const pageDimensions = { height: `${pageHeight}px`, width: `${pageWidth}px` };

    return (
        <div className={styles.analysisWrapper} style={analysisWrapperDimensions}>
            <AnalysisSideMenu
                toggleAnalysisView={toggleAnalysisView}
                currentView={opinionAnalysisView}
                isPermittedToChangeView={isPermittedToChangeView}
            />
            <div className={styles.splitWrapper}>
                {opinionAnalysisView !== AnalysisView.DATASET &&
                    <div className={styles.documentAnalysisWrapper} style={pageDimensions}>
                        <Analysis
                            selected={selectedOpinion}
                            url={url}
                            documentInView={SelectedDocument.ORIGINAL}
                            isOpening={isOpening}
                            tabOptions={tabOptions}
                            documentAnalysisView={opinionAnalysisView}
                            selectedPage={selectedPage}
                            setSelectedPage={setSelectedPage}
                            width={width}
                            type={DocumentViewerType.OPINION_ANALYSIS}
                        />
                    </div>
                }
                {opinionAnalysisView !== AnalysisView.DOCUMENT &&
                    <div className={styles.datasetAnalysisWrapper} style={pageDimensions}>
                        <OpinionInstancesWrapper />
                    </div>
                }
            </div>
        </div>
    );
};
